import { Component, Input, OnChanges } from '@angular/core'
import { ProcessingStatusDto } from 'src/app/services/dtos'
import { maxDate, shortTimeString } from 'src/app/shared/utils'

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'process-state',
  template: ` <div class="strong-text margin-top" *ngIf="this.hasStatus" [ngClass]="this.colorClass">
      {{ this.status.prefix }} {{ this.primaryText }}
    </div>
    <div class="secondary-status">{{ this.secondaryText }}</div>`,
  styleUrls: ['./process-state.component.scss'],
  standalone: false,
})
export class ProcessStateComponent {
  // @Input() justStarted: Date = null;
  @Input() ignoreBefore: Date = null
  @Input() status: ProcessingStatus

  setInputs(status: ProcessingStatus, ignoreBefore?: Date) {
    this.status = status
    this.ignoreBefore = ignoreBefore
  }

  get trueStatus(): ProcessStatus {
    return this.status.status(this.ignoreBefore)
  }

  get hasStatus(): boolean {
    return this.trueStatus !== ProcessStatus.NEVER_ATTEMPTED
  }

  // Makes the text red (error), green (success), or orange (indeterminate).
  get colorClass(): string {
    switch (this.trueStatus) {
      case ProcessStatus.NEVER_ATTEMPTED:
      case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
      case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
        return AppStatusState.INDETERMINATE.valueOf()
      case ProcessStatus.MOST_RECENT_SUCCEEDED:
        return AppStatusState.SUCCESS.valueOf()
      case ProcessStatus.MOST_RECENT_FAILED:
        return AppStatusState.ERROR.valueOf()
    }
  }

  get primaryText(): string {
    switch (this.trueStatus) {
      case ProcessStatus.NEVER_ATTEMPTED:
        return ProcessStatus.NEVER_ATTEMPTED.valueOf() // Never actually gets shown.
      case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
      case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
        return `in progress`
      case ProcessStatus.MOST_RECENT_FAILED:
        return 'error'
      case ProcessStatus.MOST_RECENT_SUCCEEDED:
        return 'succeeded'
    }
  }

  get secondaryText(): string {
    switch (this.trueStatus) {
      case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
      case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
        return `Started at ${shortTimeString(this.status.lastStart)}.`
      case ProcessStatus.NEVER_ATTEMPTED:
        return ''
      case ProcessStatus.MOST_RECENT_FAILED:
        return `Last attempt finished at ${shortTimeString(this.status.lastFailure)}.`
      case ProcessStatus.MOST_RECENT_SUCCEEDED:
        return `Succeeded at ${shortTimeString(this.status.lastSuccess)}.`
    }
  }
}

export class ProcessingStatus {
  // public updatedStart: Date = null;

  constructor(
    public name: string,
    public dto: ProcessingStatusDto
  ) {}

  get active(): boolean {
    return !!this.dto ? this.dto.active : false
  }

  get lastStart(): Date {
    return !!this.dto && this.dto.lastStart ? this.dto.lastStart : null
  }

  get lastSuccess(): Date {
    return !!this.dto && this.dto.lastSuccess ? this.dto.lastSuccess : null
  }

  get lastFailure(): Date {
    return !!this.dto && this.dto.lastFailure ? this.dto.lastFailure : null
  }

  get lastRequest(): Date {
    return !!this.dto && this.dto.lastRequest ? this.dto.lastRequest : null
  }

  get prefix(): string {
    return this.name.charAt(0).toUpperCase() + this.name.slice(1)
  }

  get inProgress(): boolean {
    switch (this.status(null)) {
      case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
      case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
        return true
      default:
        return false
    }
  }

  status(ignoreBefore: Date = null): ProcessStatus {
    if (ignoreBefore && ignoreBefore > this.lastStart) {
      return ProcessStatus.NEVER_ATTEMPTED
    } else if (this.lastStart) {
      if (this.lastSuccess || this.lastFailure) {
        if (this.lastStart > maxDate(this.lastSuccess, this.lastFailure)) {
          return ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS
        } else {
          if (this.lastFailure > this.lastSuccess) {
            return ProcessStatus.MOST_RECENT_FAILED
          } else {
            return ProcessStatus.MOST_RECENT_SUCCEEDED
          }
        }
      } else {
        return ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS
      }
    } else {
      return ProcessStatus.NEVER_ATTEMPTED
    }
  }
}

export enum ProcessStatus {
  NEVER_ATTEMPTED = 'never attempted',
  FIRST_ATTEMPT_IN_PROGRESS = 'first attempt in progress',
  SUBSEQUENT_ATTEMPT_IN_PROGRESS = 'subsequent attempt in progress',
  MOST_RECENT_FAILED = 'most recent failed',
  MOST_RECENT_SUCCEEDED = 'most recent succeeded',
}

export function computeProcessStatus(
  lastProcessRequest: Date,
  lastProcessStart: Date,
  lastProcessSuccess: Date,
  lastProcessFailure: Date
): ProcessStatus {
  const timeThresholdMinutes: number = 10 // 10 mins threshold
  const now = new Date()
  const timeThreshold = new Date(now.getTime() - timeThresholdMinutes * 60 * 1000)

  // Ignore lastProcessRequest if it's older than the threshold
  if (lastProcessRequest && lastProcessRequest < timeThreshold) {
    lastProcessRequest = null
  }

  const lastFinish = maxDate(lastProcessSuccess, lastProcessFailure)
  if (lastProcessRequest === null && lastProcessStart === null) {
    return ProcessStatus.NEVER_ATTEMPTED
  } else if (lastProcessSuccess === null && lastProcessFailure === null) {
    return ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS
  } else if (lastProcessRequest > lastFinish || lastProcessStart > lastFinish) {
    return ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS
  } else if (lastProcessFailure > lastProcessStart) {
    return ProcessStatus.MOST_RECENT_FAILED
  } else {
    return ProcessStatus.MOST_RECENT_SUCCEEDED
  }
}

export enum AppStatusState {
  INDETERMINATE = 'indeterminate',
  SUCCESS = 'success',
  ERROR = 'error',
}
